import type { ReactNode } from "react"
import { CircleAlert, Inbox } from "lucide-react"

import { Callout } from "../ui/callout"
import { cn } from "../../lib/utils"

type QueryStateProps = {
  isLoading: boolean
  error: unknown
  isEmpty?: boolean
  emptyTitle?: string
  emptyMessage?: string
  embedded?: boolean
  children: ReactNode
}

export function QueryState({
  isLoading,
  error,
  isEmpty = false,
  emptyTitle = "No results",
  emptyMessage = "Not found.",
  embedded = false,
  children,
}: QueryStateProps): ReactNode {
  if (isLoading) {
    return <QuerySkeleton embedded={embedded} />
  }
  if (error) {
    return (
      <Callout
        tone="destructive"
        icon={<CircleAlert className="size-4" />}
        title="This view could not be loaded"
        {...(embedded ? { className: "rounded-none border-0 shadow-none" } : {})}
      >
        <p className="mt-2 text-sm text-foreground/80">{(error as Error).message}</p>
      </Callout>
    )
  }
  if (isEmpty) {
    return (
      <div
        className={cn(
          "flex min-h-48 flex-col items-center justify-center bg-card px-6 py-10 text-center",
          embedded ? "" : "rounded-xl border border-dashed border-border"
        )}
      >
        <span className="inline-flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Inbox className="size-4" />
        </span>

        <div className="mt-3 text-sm font-medium text-foreground">{emptyTitle}</div>

        <p className="mt-1 max-w-md text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    )
  }

  return children
}

function QuerySkeleton({ embedded }: { embedded: boolean }) {
  return (
    <div
      className={cn("overflow-hidden bg-card", embedded ? "" : "rounded-xl border border-border")}
      role="status"
      aria-label="Loading content"
    >
      <div className="flex items-center gap-3 border-b border-border bg-muted/40 px-5 py-4">
        <span className="size-4 animate-pulse rounded bg-muted" />

        <div className="h-3 w-32 animate-pulse rounded-full bg-muted" />
      </div>

      <div className="divide-y divide-border/70">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="flex items-center gap-4 px-5 py-4">
            <div className="size-8 animate-pulse rounded-lg bg-muted" />

            <div className="h-3 flex-1 animate-pulse rounded-full bg-muted" />

            <div className="h-3 w-24 animate-pulse rounded-full bg-muted" />
          </div>
        ))}
      </div>
    </div>
  )
}
